import { useCallback, useEffect, useState } from 'react'
import {
  createCustomLocation,
  deactivateCustomLocation,
  getAllSlopes,
  getCustomLocations,
  updateCustomLocation,
  getLiftsByResort,
} from '../../../../api/endpoints'
import { buildLocationsByType } from '../utils/locationMerge'
import { toast } from '../../../../lib/toast'
import { validateCustomLocation } from '../utils/validations'

/**
 * Hook for managing locations (slopes, custom locations, and lifts)
 * @returns {Object} Locations state and methods
 */
const useLocations = () => {
  // --- STATE ---
  const [slopes, setSlopes] = useState([])
  const [customLocations, setCustomLocations] = useState([])
  const [lifts, setLifts] = useState([]) // New State
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [locationType, setLocationType] = useState('slope')

  const [searchText, setSearchText] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const [editingLocation, setEditingLocation] = useState(null)

  const [loadingSlopes, setLoadingSlopes] = useState(true)
  const [loadingCustomLocations, setLoadingCustomLocations] = useState(true)
  const [loadingLifts, setLoadingLifts] = useState(true) // New loading state
  const [actionLoading, setActionLoading] = useState(false)
  const [error, setError] = useState(null)

  // --- API FETCH FUNCTIONS ---

  /**
   * Fetch all slopes from API
   */
  const fetchSlopes = useCallback(async () => {
    try {
      setLoadingSlopes(true)
      setError(null)

      const userInfo = JSON.parse(localStorage.getItem('user'))
      const resortId = userInfo?.user?.resort_id

      if (!resortId) {
        throw new Error("L'identifiant du site n'a pas été trouvé")
      }

      const data = await getAllSlopes(resortId)
      setSlopes(data || [])
    } catch (err) {
      console.error('Error fetching slopes:', err)
      const errorMsg = `Impossible de charger les pistes: ${err.message}`
      setError(errorMsg)
      setSlopes([])
    } finally {
      setLoadingSlopes(false)
    }
  }, [])

  /**
   * Fetch custom locations from API
   */
  const fetchCustomLocations = useCallback(async () => {
    try {
      setLoadingCustomLocations(true)
      setError(null)

      const userInfo = JSON.parse(localStorage.getItem('user'))
      const resortId = userInfo?.user?.resort_id

      if (!resortId) {
        throw new Error("L'identifiant du site n'a pas été trouvé")
      }

      const data = await getCustomLocations(resortId)
      setCustomLocations(data || [])
    } catch (err) {
      console.error('Error fetching custom locations:', err)
      const errorMsg = `Impossible de charger les emplacements personnalisés: ${err.message}`
      setError(errorMsg)
      setCustomLocations([])
    } finally {
      setLoadingCustomLocations(false)
    }
  }, [])

  /**
   * Fetch Lifts from API
   * MOVED UP to occur before useEffect
   */
  const fetchLifts = useCallback(async () => {
    try {
      setLoadingLifts(true)
      const userInfo = JSON.parse(localStorage.getItem('user'))
      const resortId = userInfo?.user?.resort_id

      if (!resortId) throw new Error("Resort ID not found")

      const data = await getLiftsByResort(resortId)
      setLifts(data || [])
    } catch (err) {
      console.error('Error fetching lifts:', err)
      setLifts([])
    } finally {
      setLoadingLifts(false)
    }
  }, [])

  /**
   * Initialize data on component mount
   */
  useEffect(() => {
    fetchSlopes()
    fetchCustomLocations()
    fetchLifts() 
  }, [fetchSlopes, fetchCustomLocations, fetchLifts])

  // --- LOGIC HELPERS ---

  /**
   * Get locations based on selected type
   */
  const getLocationsByType = useCallback(
    () => buildLocationsByType({ locationType, slopes, lifts, customLocations }),
    [locationType, slopes, lifts, customLocations],
  )

  /**
   * Get filtered locations based on search text
   */
  const getFilteredLocations = useCallback(() => {
    const locations = getLocationsByType()
    if (!searchText.trim()) return locations

    const searchLower = searchText.toLowerCase()
    return locations.filter(location => {
      const name = (location.nom || location.name || '').toLowerCase()
      const sector = (location.sector || '').toLowerCase()
      // For lifts, maybe check type too (e.g. TSD, TK)
      const type = (location.type || '').toLowerCase()
      
      return name.includes(searchLower) || sector.includes(searchLower) || type.includes(searchLower)
    })
  }, [getLocationsByType, searchText])

  /**
   * Handle location selection
   * @param {Object|null} location - Selected location or null to clear
   */
  const handleLocationSelect = useCallback(location => {
    setSelectedLocation(location)
    setSearchText(location ? location.nom || location.name : '')
    setShowDropdown(false)
  }, [])

  // --- CRUD ACTIONS ---

  /**
   * Add a new custom location
   * @param {Object} locationData - Location data to add
   * @returns {Promise<Object>} Created location
   */
  const addCustomLocation = async locationData => {
    const validationInput = {
      name: locationData.name,
      coordinates: { lat: locationData.lat, lng: locationData.long },
      location_type: locationData.location_type,
    }

    const { isValid, errors } = validateCustomLocation(validationInput)

    if (!isValid) {
      const errorMsg = Object.values(errors).join(', ')
      toast.error(errorMsg)
      throw new Error(errorMsg)
    }

    try {
      setActionLoading(true)
      setError(null)

      const userInfo = JSON.parse(localStorage.getItem('user'))
      const resortId = userInfo?.user?.resort_id

      if (!resortId) {
        throw new Error('Resort ID not found')
      }

      const newLocation = await createCustomLocation({
        ...locationData,
        resort_id: resortId,
      })

      setCustomLocations(prevLocations => [...prevLocations, newLocation])

      toast.success(`Emplacement "${locationData.name}" créé avec succès`)

      return newLocation
    } catch (err) {
      console.error('Error adding custom location:', err)
      const errorMsg =
        err?.response?.status === 409
          ? 'Un emplacement portant ce nom existe déjà sur ce site.'
          : `Impossible de créer l'emplacement: ${err.message}`
      setError(errorMsg)
      toast.error(errorMsg)
      throw err
    } finally {
      setActionLoading(false)
    }
  }

  /**
   * Update an existing custom location
   * @param {number|string} id - Location ID
   * @param {Object} locationData - Updated location data
   * @returns {Promise<Object>} Updated location
   */
  const updateCustomLocationById = async (id, locationData) => {
    const validationInput = {
      name: locationData.name,
      coordinates: { lat: locationData.lat, lng: locationData.long },
      location_type: locationData.location_type,
    }

    const { isValid, errors } = validateCustomLocation(validationInput)

    if (!isValid) {
      const errorMsg = Object.values(errors).join(', ')
      toast.error(errorMsg)
      throw new Error(errorMsg)
    }

    try {
      setActionLoading(true)
      setError(null)

      const updatedLocations = customLocations.map(loc =>
        loc.id === id ? { ...loc, ...locationData } : loc,
      )
      setCustomLocations(updatedLocations)

      if (selectedLocation?.id === id) {
        setSelectedLocation({ ...selectedLocation, ...locationData })
      }

      await updateCustomLocation(id, locationData)

      toast.success(`Emplacement "${locationData.name}" mis à jour avec succès`)

      return { id, ...locationData }
    } catch (err) {
      console.error('Error updating custom location:', err)
      const errorMsg =
        err?.response?.status === 409
          ? 'Un emplacement portant ce nom existe déjà sur ce site.'
          : `Impossible de mettre à jour l'emplacement: ${err.message}`
      setError(errorMsg)
      toast.error(errorMsg)

      fetchCustomLocations()
      throw err
    } finally {
      setActionLoading(false)
    }
  }

  /**
   * Delete a custom location
   * @param {number|string} id - Location ID
   * @returns {Promise<void>}
   */
  const deleteCustomLocationById = async id => {
    try {
      setActionLoading(true)
      setError(null)

      const locationToDelete = customLocations.find(loc => loc.id === id)
      const locationName = locationToDelete?.name || "l'emplacement"

      setCustomLocations(prevLocations =>
        prevLocations.filter(loc => loc.id !== id),
      )

      if (selectedLocation?.id === id) {
        setSelectedLocation(null)
        setSearchText('')
      }

      await deactivateCustomLocation(id)

      toast.success(`"${locationName}" supprimé avec succès`)
    } catch (err) {
      console.error('Error deleting custom location:', err)
      const errorMsg = `Impossible de supprimer l'emplacement: ${err.message}`
      setError(errorMsg)
      toast.error(errorMsg)

      fetchCustomLocations()
      throw err
    } finally {
      setActionLoading(false)
    }
  }

  /**
   * Set the location to edit
   * @param {Object|null} location - Location to edit or null to clear
   */
  const setEditLocation = location => {
    setEditingLocation(location)
  }

  /**
   * Clear any errors
   */
  const clearError = () => {
    setError(null)
  }

  return {
    slopes,
    customLocations,
    lifts, // Export lifts
    selectedLocation,
    locationType,
    filteredLocations: getFilteredLocations(),

    searchText,
    showDropdown,
    editingLocation,

    loading: loadingSlopes || loadingCustomLocations || loadingLifts,
    loadingSlopes,
    loadingCustomLocations,
    actionLoading,
    error,

    setLocationType,
    setSearchText,
    setShowDropdown,
    handleLocationSelect,
    addCustomLocation,
    updateCustomLocationById,
    deleteCustomLocationById,
    setEditLocation,
    clearError,
    fetchSlopes,
    fetchCustomLocations,
    fetchLifts 
  }
}

export default useLocations